import PropTypes from 'prop-types';
import OptionCard from '../OptionCard';
import { useMemo, useRef, useCallback } from 'react';
import FJLocalStore from '../../../../../store/FJLocalStore';
import FJConfig from '../../../../../config/FJConfig';
import FJHeadshotUtil from '../../../../../util/FJHeadshotUtil';
import FJFileHandleUtil from '../../../../../util/FJFileHandleUtil';

import styles from './StepForm.module.less';

const FJThemeSelector = ({ selectedTheme, onThemeChange, gender, selectedCategoryKey, selectedProfession }) => {
    const cardRefs = useRef({});

    const isDisabled = useMemo(() => {
        return selectedTheme.length >= 10;
    }, [selectedTheme]);

    const themeList = useMemo(() => {
        return FJHeadshotUtil.getThemesByProfessionAndGender(selectedProfession, selectedCategoryKey, gender);
    }, [gender, selectedCategoryKey, selectedProfession]);

    const triggerSelectionAnimation = useCallback(
        optionKey => {
            const cardElement = cardRefs.current[optionKey];
            if (!cardElement) return;

            // 获取 stepper 元素作为参考窗口
            const stepperElement = document.getElementById('stepper');
            if (!stepperElement) return;

            // 获取卡片位置（相对于视口）
            const cardRect = cardElement.getBoundingClientRect();
            const stepperRect = stepperElement.getBoundingClientRect();

            // 计算 stepper 中心底部位置（相对于视口）
            const stepperCenterX = stepperRect.left + stepperRect.width / 2;
            const stepperBottomY = stepperRect.bottom - 100; // 距离 stepper 底部100px

            // 计算目标位置（相对于卡片当前位置）
            const targetX = stepperCenterX - cardRect.left - 130; // 130px 是卡片宽度的一半
            const targetY = stepperBottomY - cardRect.top - 30; // 20px 是按钮threshold
            const time = 400;
            // 创建动画元素（固定定位，相对于视口）
            const animatedElement = document.createElement('div');
            animatedElement.className = styles.animated_card;
            animatedElement.style.cssText = `
                position: fixed;
                left: ${cardRect.left}px;
                top: ${cardRect.top}px;
                width: 260px;
                height: 324px;
                z-index: 10000;
                pointer-events: none;
                background: url(${FJFileHandleUtil.getImageUrlWithFormat(
                    `${FJConfig.headshotImagePath}${themeList.find(t => t.key === optionKey)?.imageUrl}?v=${FJConfig.version}`,
                )}) center/cover;
                border-radius: 12px;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
                transform: scale(1);
                opacity: 1;
            `;

            // 添加到 body
            document.body.appendChild(animatedElement);

            // 计算抛物线中间点（向上20px）
            const midTime = 0; // 60% 时间到达最高点

            // 第一阶段：向上到最高点
            // requestAnimationFrame(() => {
            //     animatedElement.style.transition = `transform ${midTime}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`;
            //     animatedElement.style.transform = `translate(${targetX / 2}px, ${-126}px) scale(0.6)`;
            // });

            // 第二阶段：向下到目标位置
            setTimeout(() => {
                animatedElement.style.transition = `transform ${time - midTime}ms cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity ${time - midTime}ms ease-out`;
                animatedElement.style.transform = `translate(${targetX}px, ${targetY}px) scale(0.2)`;
                animatedElement.style.opacity = '0.3';
            }, midTime);

            // 清理动画元素
            setTimeout(() => {
                if (animatedElement.parentNode) {
                    animatedElement.parentNode.removeChild(animatedElement);
                }
            }, time);
        },
        [themeList],
    );

    const handleThemeChange = useCallback(
        option => {
            // 检查当前选中的主题数量
            const currentSelectedCount = selectedTheme.length;
            const isCurrentlySelected = selectedTheme.findIndex(item => item.key === option.key) !== -1;

            // 如果当前只有一个主题被选中，且用户要取消选择它，则禁用撤销
            if (currentSelectedCount === 1 && isCurrentlySelected) {
                // 禁用撤销功能 - 不允许取消最后一个选项
                return;
            }

            if (isDisabled && !isCurrentlySelected) {
                return;
            }

            // 如果是选中操作，触发动画
            if (!isCurrentlySelected) {
                triggerSelectionAnimation(option.key);
            }

            // 否则正常处理选择变化
            onThemeChange(option);
        },
        [selectedTheme, isDisabled, onThemeChange, triggerSelectionAnimation],
    );

    const setCardRef = useCallback(
        optionKey => element => {
            if (element) {
                cardRefs.current[optionKey] = element;
            }
        },
        [],
    );

    return (
        <div className={styles.step_form}>
            <h3 className={styles.step_title}>
                {FJLocalStore._('select-your-theme')
                    .replace('xxx', 3)
                    .replace('yyy', themeList.length > 10 ? 10 : themeList.length)}
            </h3>
            <div className={styles.options_grid}>
                {themeList.map(option => (
                    <div key={option.key} ref={setCardRef(option.key)}>
                        <OptionCard
                            title={FJLocalStore._(option.key)}
                            imageUrl={FJFileHandleUtil.getImageUrlWithFormat(
                                `${FJConfig.headshotImagePath}${option.imageUrl}?v=${FJConfig.version}`,
                            )}
                            isSelected={selectedTheme.findIndex(item => item.key === option.key) !== -1}
                            onClick={() => handleThemeChange(option)}
                            multiple
                            disabled={isDisabled}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

FJThemeSelector.propTypes = {
    selectedTheme: PropTypes.array,
    onThemeChange: PropTypes.func,
    gender: PropTypes.string,
    selectedCategoryKey: PropTypes.string,
    selectedProfession: PropTypes.object,
};

export default FJThemeSelector;
